'use client';

import { RefObject } from 'react';
import { useIntl } from 'react-intl';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { H3 } from '@osmosis/react-ui/components/Text';
import { textOrange } from '@osmosis/tailwind-ui/styles/text';
import { InlineSpinner } from './InlineSpinner';

type Props = {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  iframeSrc: string | null;
  iframeLoading: boolean;
  iframeError: boolean;
  onRetry: () => void;
  /** Prefixes all data-testids; defaults preserve AIPanel's existing test ids. */
  testIdPrefix?: string;
};

/**
 * Shared OAI iframe body (loading spinner, retry-on-error state, iframe) used
 * by both the resizable AIPanel and the full-page /osmosisAI route.
 */
export const OAIIframeBody = ({
  iframeRef,
  iframeSrc,
  iframeLoading,
  iframeError,
  onRetry,
  testIdPrefix = 'ai-panel',
}: Props) => {
  const { formatMessage } = useIntl();

  return (
    <Flex
      className="relative flex-1 items-start justify-center"
      data-testid={`${testIdPrefix}-body`}
      role="status"
      aria-label={formatMessage({
        id: 'ai-panel.loading',
        defaultMessage: 'Loading AI assistant',
      })}
    >
      {iframeLoading && <InlineSpinner />}
      {iframeError && (
        <Flex
          className="flex-col items-center gap-3 self-start pt-8"
          data-testid={`${testIdPrefix}-error`}
        >
          <H3 className={textOrange}>
            {formatMessage({
              id: 'ai-panel.error',
              defaultMessage: 'Failed to load AI chat.',
            })}
          </H3>
          <Button
            data-testid={`${testIdPrefix}-retry-btn`}
            onClick={onRetry}
            variant="outline"
            className="rounded-lg border border-black px-4 py-2"
          >
            {formatMessage({ id: 'ai-panel.retry', defaultMessage: 'Retry' })}
          </Button>
        </Flex>
      )}
      {iframeSrc && !iframeError && (
        <Box
          className="h-full w-full"
          data-testid={`${testIdPrefix}-iframe-wrapper`}
        >
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            className="h-full w-full border-0"
            data-testid={`${testIdPrefix}-iframe`}
            allow="clipboard-write; fullscreen"
            allowFullScreen
            title={formatMessage({
              id: 'ai-panel.iframe-title',
              defaultMessage: 'Osmosis AI Chat',
            })}
          />
        </Box>
      )}
    </Flex>
  );
};
